/**
 * ตัวช่วยเลือกอุปกรณ์ในฟอร์มการเช่า (หน้าแก้ไข) — ต่อ "รายการ" ไม่ใช่ต่อหน้า
 *
 * BUG (feedback amintra 3 ก.ย. 2026, /rentals/edit/90): หน้าแก้ไขเก็บรายชื่ออุปกรณ์ที่เลือกได้ไว้ใน state เดียว
 * ใช้ร่วมกันทุกรายการ พอรายการที่ 2 เปลี่ยน "ประเภทอุปกรณ์" ตัวจัดการฟอร์มเขียนทับ state ให้เหลือแต่ประเภทใหม่
 * → รายการที่ 1 (ประเภทเดิม) ไม่เหลือตัวเลือก ช่องกลับเป็น "-- เลือกอุปกรณ์ --" และค้นหา SN ไม่เจอ
 * แก้โดยให้แต่ละรายการกรองตัวเลือกของตัวเองจากรายชื่อรวม (ไม่แตะ state ร่วม) และล้าง device_id
 * เฉพาะรายการที่เปลี่ยนประเภทและเครื่องเดิมไม่ตรงประเภทใหม่
 */

import { excludePendingReturn, type PendingReturnLike } from './devicePickerFilter';

export interface DeviceLike extends Partial<PendingReturnLike> {
  id: number;
  device_type_id?: number | null;
  status?: string | null;
}

const AVAILABLE_STATUSES = new Set(['AVAILABLE', 'available', 'พร้อมใช้งาน']);

export function isDeviceAvailable(device: DeviceLike): boolean {
  return AVAILABLE_STATUSES.has(String(device.status ?? ''));
}

/**
 * อุปกรณ์ที่รายการหนึ่งเลือกได้: ตรงประเภทของรายการนั้น และ (ว่าง หรือ เป็นเครื่องที่อยู่ในสัญญานี้อยู่แล้ว)
 * และ **ไม่มีใบตามเก็บค้าง** (R4U-2 — เว้นเครื่องของสัญญานี้เอง · ดู utils/devicePickerFilter)
 */
export function devicesForItem<T extends DeviceLike>(
  devices: T[],
  deviceTypeId: number | '' | null | undefined,
  rentalDeviceIds: Array<number | '' | null | undefined> = [],
): T[] {
  if (!deviceTypeId) return [];
  const inRental = new Set(rentalDeviceIds.filter((id): id is number => typeof id === 'number' && id > 0));
  const candidates = devices.filter(
    (d) => d.device_type_id === deviceTypeId && (isDeviceAvailable(d) || inRental.has(d.id)),
  );
  return excludePendingReturn(candidates, inRental);
}

/** เมื่อรายการเปลี่ยนประเภท: ต้องล้างเครื่องที่เลือกไว้หรือไม่ (เครื่องเดิมไม่อยู่ในตัวเลือกของประเภทใหม่) */
export function shouldClearDeviceOnTypeChange<T extends DeviceLike>(
  devices: T[],
  newDeviceTypeId: number | '' | null | undefined,
  currentDeviceId: number | '' | null | undefined,
  rentalDeviceIds: Array<number | '' | null | undefined> = [],
): boolean {
  if (!currentDeviceId) return false;
  return !devicesForItem(devices, newDeviceTypeId, rentalDeviceIds).some((d) => d.id === currentDeviceId);
}
